(() => {
  'use strict';
  if (window.StudioPhoneStory) return;
  const scenes = new Map();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const shortWindow = matchMedia('(max-height: 559px)');
  const clamp = value => Math.max(0, Math.min(1, value));
  const smooth = value => { const p = clamp(value); return p * p * (3 - 2 * p); };
  const segment = (p, start, end) => smooth((p - start) / (end - start));
  const mix = (start, end, p) => start + (end - start) * p;
  const steps = [.27, .53, 1];
  let frame = 0;
  let last = 0;

  function motion(el) {
    return !reduced.matches && !shortWindow.matches && document.body.dataset.motionEnabled !== 'false' &&
      el.dataset.animationEnabled !== 'false';
  }

  function paint(el, state, p) {
    const approach = segment(p, 0, .28);
    const contact = segment(p, .28, .46);
    const collect = segment(p, .66, .96);
    const left = segment(p, .69, .94);
    const right = segment(p, .75, 1);
    const set = (name, value) => el.style.setProperty('--at-scan-' + name, value);

    // The same case lands over the phone rim, then joins the collection.
    set('tag-x', mix(-250 * (1 - approach), 0, collect).toFixed(3) + '%');
    set('tag-y', mix(-24 * (1 - approach) + 14 * contact, 132, collect).toFixed(3) + '%');
    set('tag-rotate', mix(-24 * (1 - approach) + 1 * approach, 2, collect).toFixed(3) + 'deg');
    set('tag-opacity', segment(p, 0, .08).toFixed(3));
    set('phone-tilt', (3 * (1 - approach)).toFixed(3) + 'deg');
    set('phone-y', (collect * 115).toFixed(3) + '%');
    set('phone-opacity', (1 - segment(p, .76, .93)).toFixed(3));
    set('album-open', segment(p, .30, .48).toFixed(3));
    set('notification', (segment(p, .21, .34) * (1 - collect)).toFixed(3));
    set('signal', (segment(p, .20, .31) * (1 - collect) * .6).toFixed(3));
    set('ring-scale', '.55');
    set('collection-left-x', (-155 * (1 - left)).toFixed(3) + '%');
    set('collection-right-x', (155 * (1 - right)).toFixed(3) + '%');
    set('collection-left-y', (35 * (1 - left)).toFixed(3) + '%');
    set('collection-right-y', (35 * (1 - right)).toFixed(3) + '%');
    set('collection-left-rotate', mix(-35, -13, left).toFixed(3) + 'deg');
    set('collection-right-rotate', mix(35, 13, right).toFixed(3) + 'deg');
    set('collection-left-opacity', segment(p, .69, .83).toFixed(3));
    set('collection-right-opacity', segment(p, .75, .89).toFixed(3));

    const pose = prefix => ({
      x: parseFloat(el.style.getPropertyValue('--at-scan-' + prefix + '-x')),
      y: parseFloat(el.style.getPropertyValue('--at-scan-' + prefix + '-y')),
      angle: parseFloat(el.style.getPropertyValue('--at-scan-' + prefix + '-rotate'))
    });
    state.keychain?.paint(p, [pose('collection-left'), pose('tag'), pose('collection-right')]);

    const phase = p < .30 ? 0 : p < .66 ? 1 : 2;
    if (phase !== state.phase) {
      state.buttons.forEach((button, index) => button.setAttribute('aria-pressed', String(index === phase)));
      el.dataset.phase = ['tap', 'stream', 'collect'][phase];
      state.phase = phase;
    }
    el.dataset.progress = p.toFixed(3);
  }

  function update(now) {
    frame = 0;
    const dt = Math.min(160, now - (last || now));
    last = now;
    let again = false;
    for (const [el, state] of scenes) {
      if (!el.isConnected) { state.keychain?.destroy(); scenes.delete(el); continue; }
      const animate = motion(el);
      if (state.animate !== animate) {
        state.animate = animate;
        state.p = null;
        state.staticProgress = .53;
      }
      const rect = state.track.getBoundingClientRect();
      if (animate && (rect.bottom < 0 || rect.top > innerHeight)) continue;
      const stickyTop = parseFloat(getComputedStyle(state.sticky).top) || 0;
      const distance = Math.max(1, state.track.offsetHeight - state.sticky.offsetHeight);
      const target = animate ? clamp((stickyTop - rect.top) / distance) : state.staticProgress;
      state.p = state.p == null || !animate ? target : state.p + (target - state.p) * (1 - Math.exp(-dt / 85));
      if (Math.abs(target - state.p) < .0005) state.p = target;
      else again = true;
      paint(el, state, state.p);
    }
    if (again && !document.hidden) frame = requestAnimationFrame(update);
  }

  function queue() {
    if (!frame && !document.hidden) {
      last = performance.now();
      frame = requestAnimationFrame(update);
    }
  }

  function init(scope = document) {
    const elements = [...scope.querySelectorAll('[data-at-nfc-scan]')];
    if (scope.matches && scope.matches('[data-at-nfc-scan]')) elements.unshift(scope);
    elements.forEach(el => {
      if (scenes.has(el)) return;
      const track = el.querySelector('.studio-phone-track');
      const sticky = el.querySelector('.studio-phone-sticky');
      if (!track || !sticky) return;
      const animate = motion(el);
      const state = { track, sticky, buttons: [...el.querySelectorAll('[data-scan-seek]')], p: null, phase: null, animate, staticProgress: .53, keychain: window.StudioCollectionKeychain?.create(el) };
      scenes.set(el, state);
      paint(el, state, animate ? 0 : .53);
    });
    queue();
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('[data-scan-seek]');
    if (!button) return;
    const el = button.closest('[data-at-nfc-scan]');
    const state = scenes.get(el);
    const p = steps[Number(button.dataset.scanSeek)];
    if (!state || p == null) return;
    if (!motion(el)) {
      state.staticProgress = p;
      state.p = p;
      paint(el, state, p);
      return;
    }
    const top = parseFloat(getComputedStyle(state.sticky).top) || 0;
    const distance = Math.max(0, state.track.offsetHeight - state.sticky.offsetHeight);
    scrollTo({ top: scrollY + state.track.getBoundingClientRect().top - top + p * distance, behavior: 'smooth' });
  });

  addEventListener('scroll', queue, { passive: true });
  addEventListener('resize', queue);
  reduced.addEventListener('change', queue);
  shortWindow.addEventListener('change', queue);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) { cancelAnimationFrame(frame); frame = 0; }
    else queue();
  });
  new MutationObserver(queue).observe(document.body, { attributes: true, attributeFilter: ['data-motion-enabled'] });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const el of scenes.keys()) {
      if (!el.isConnected || event.target.contains(el)) { scenes.get(el).keychain?.destroy(); scenes.delete(el); }
    }
  });
  window.StudioPhoneStory = { init };
  init();
})();
